import React from 'react';
import Type from 'prop-types';
import { noop, camelCase } from 'lodash';
import Dropzone from 'react-dropzone';
import { useSnackbar } from 'notistack';
import cn from 'classnames';
import imageCompression from 'browser-image-compression';
import { useTranslation } from 'hooks';
import { formatBytes } from 'utils/unit';
import { MAX_FILE_SIZE } from 'constants/units';
import styles from './FilePicker.module.css';

const defaultImageCompressionOptions = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
};

// TODO Вернуть компрессию когда разберемся почему не работет

const FilePicker = (props) => {
  const {
    children,
    inputProps,
    className,
    maxFiles,
    maxSize,
    minSize,
    onDrop,
    onDropAccepted,
    onDropRejected,
    useImageCompression,
    imageCompressionOptions,
    ...dropzoneProps
  } = props;
  const compressionOptions = { ...defaultImageCompressionOptions, imageCompressionOptions };

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const compressImageFiles = (rawFiles) => {
    return Promise.all(rawFiles.map(async (file) => {
      try {
        return await imageCompression(file, compressionOptions);
      } catch (error) {
        return file; // Возвращаем исходный файл в случае ошибки
      }
    }));
  };

  const handleDropAccepted = async (acceptedFiles, event) => {
    // TODO Убрать
    // eslint-disable-next-line no-console
    console.log("handleDropAccepted");
    if (!useImageCompression) {
      onDrop(acceptedFiles, event);
    } else {
      try {
        enqueueSnackbar(t('compressingPhoto'));
        const compressedFiles = await compressImageFiles(acceptedFiles);
        onDrop(compressedFiles, event);
      } catch (error) {
        enqueueSnackbar(error.message, { variant: 'error' });
      }
    }
  };

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'fileInvalidType':
        return t('fileInvalidType');
      case 'fileTooLarge':
        return `${t('fileTooLarge')} ${formatBytes(maxSize)}`;
      case 'fileTooSmall':
        return `${t('fileTooSmall')} ${formatBytes(minSize)}`;
      case 'tooManyFiles':
        return `${t('tooManyFiles')} ${maxFiles}`;
      default:
        return '';
    }
  };

  const handleDropRejected = (rejectedFiles, event) => {
    const { code } = rejectedFiles[0].errors[0];
    const errorCode = camelCase(code);
    const errorMessage = getErrorMessage(errorCode);
    enqueueSnackbar(errorMessage, { variant: 'error' });

    onDropRejected(rejectedFiles, event);
  };

  return (
    <Dropzone
      {...dropzoneProps}
      maxSize={maxSize}
      minSize={minSize}
      maxFiles={maxFiles}
      onDropAccepted={handleDropAccepted}
      onDropRejected={handleDropRejected}
    >
      {({ getRootProps, getInputProps }) => (
        <span
          {...getRootProps({
            className: cn(styles.root, className),
          })}
        >
          <input {...getInputProps(inputProps)} />
          {children}
        </span>
      )}
    </Dropzone>
  );
};

FilePicker.propTypes = {
  children: Type.node,
  className: Type.string,
  inputProps: Type.object, // eslint-disable-line react/forbid-prop-types,
  maxFiles: Type.number,
  maxSize: Type.number,
  minSize: Type.number,
  noDrag: Type.bool,
  multiple: Type.bool,
  useImageCompression: Type.bool,
  /**
   * See [Browser Image Compression](https://github.com/Donaldcwl/browser-image-compression#api)
   * all available autocomplete option
   */
  imageCompressionOptions: Type.shape({
    maxSizeMB: Type.number,
    maxWidthOrHeight: Type.number,
    onProgress: Type.func,
    useWebWorker: Type.bool,
    maxIteration: Type.number,
    exifOrientation: Type.number,
    fileType: Type.string,
    initialQuality: Type.number,
  }),
  onDrop: Type.func,
  onDropAccepted: Type.func,
  onDropRejected: Type.func,
  onRetry: Type.func,
};

FilePicker.defaultProps = {
  children: null,
  inputProps: undefined,
  className: undefined,
  maxFiles: 1,
  maxSize: MAX_FILE_SIZE,
  minSize: 0,
  multiple: false,
  noDrag: true,
  useImageCompression: false,
  imageCompressionOptions: {},
  onDrop: noop,
  onDropAccepted: noop,
  onDropRejected: noop,
  onRetry: noop,
};

export default FilePicker;
